import {
	ConfidenceLevel,
	type DataSourceDetail,
	DataSourceDetailDataSourceTypeEnum,
	DataSourceDetailSourceDescriptionEnum,
	type DataSources,
	DataSourcesDataSourceTypeEnum,
	type DraftDataSourceDetail,
	type DraftDataSources,
	type DraftEmissionRequestFormData,
	type DraftEmissionsAttachment,
	type DraftEmissionsValuesBreakdown,
	type DraftEmissionValueItem,
	type DraftEmissionValues,
	DraftEmissionValuesAllocationMethodUsedEnum,
	type DraftEmissionValuesLocation,
	type DraftMethodologies,
	type DraftMethodologyStandards,
	type DraftVerificationAttachment,
	type DraftVerificationData,
	type EmissionRequestProductFormData,
	type EmissionsAttachment,
	type EmissionsValuesBreakdown,
	EmissionType,
	EmissionValueDataUsed,
	EmissionValueGranularityLevel,
	type EmissionValueItem,
	type EmissionValues,
	type EmissionValuesLocation,
	EmissionValuesLocationCountryEnum,
	EmissionValuesLocationLocationTypeEnum,
	EmissionValuesLocationRegionEnum,
	EmissionValueUnit,
	InitiativeLevel,
	LifecycleStage,
	ManufacturingLocationCountryEnum,
	ManufacturingLocationLocationTypeEnum,
	ManufacturingLocationRegionEnum,
	type Methodologies,
	MethodologiesImpactAssessmentMethodEnum,
	type MethodologyStandards,
	MethodologyStandardsStandardCompliantEnum,
	Standard,
	type VerificationAttachment,
	type VerificationData,
	VerificationProvider,
} from '@api-client/api'

// Function overloads for type safety
function convertToEnum<T>(
	value: string | undefined | null,
	enumType: Record<string, T>,
	name: string,
	returnUndefinedIfInvalid: true
): T | undefined
function convertToEnum<T>(
	value: string,
	enumType: Record<string, T>,
	name: string,
	returnUndefinedIfInvalid?: false
): T
function convertToEnum<T>(
	value: string | undefined | null,
	enumType: Record<string, T>,
	name: string,
	returnUndefinedIfInvalid = false
): T | undefined {
	if (!value || !Object.values(enumType).includes(value as T)) {
		if (returnUndefinedIfInvalid) {
			return undefined
		}
		throw new Error(`${value} is not a valid ${name}`)
	}
	return value as T
}

const convertDraftToDataSourcesDetails = (
	draftData: DraftDataSourceDetail | DataSourceDetail,
	fallbackDataSourceType?: DataSourceDetailDataSourceTypeEnum
): DataSourceDetail => {
	const {
		dataSourceType: rawDataSourceType,
		sourceDescription,
		...others
	} = draftData
	const dataSourceType = rawDataSourceType ?? fallbackDataSourceType
	if (!dataSourceType) {
		throw new Error('The type of a data source (primary/secondary) is not set')
	}
	if (!sourceDescription) {
		throw new Error('The description of a data source is not set')
	}
	return {
		dataSourceType: convertToEnum(
			dataSourceType,
			DataSourceDetailDataSourceTypeEnum,
			'data source type'
		),
		sourceDescription: convertToEnum(
			sourceDescription,
			DataSourceDetailSourceDescriptionEnum,
			'data source description'
		),
		...others,
	}
}

const convertDraftToDataSources = (
	draftData?: DraftDataSources | DataSources
): DataSources => {
	if (!draftData) {
		throw new Error('Missing data sources')
	}
	const { dataSourceDetails, dataSourceType, ...others } = draftData
	if (!dataSourceType) {
		throw new Error('No data source type defined')
	}
	return {
		dataSourceType: convertToEnum(
			dataSourceType,
			DataSourcesDataSourceTypeEnum,
			'data source type'
		),
		dataSourceDetails: (dataSourceDetails ?? []).map(item =>
			convertDraftToDataSourcesDetails(
				item,
				DataSourceDetailDataSourceTypeEnum.PRIMARY
			)
		),
		...others,
	}
}

const convertDraftToMethodologyStandards = (
	draftData?: DraftMethodologyStandards | MethodologyStandards
): MethodologyStandards => {
	if (!draftData) {
		throw new Error('Missing methodology standards')
	}
	const { standardCompliant, standardUsed, ...others } = draftData
	const finalStandardCompliant =
		standardCompliant ??
		(standardUsed && standardUsed !== Standard.OTHER
			? MethodologyStandardsStandardCompliantEnum.YES
			: MethodologyStandardsStandardCompliantEnum.UNKNOWN)
	return {
		standardCompliant: convertToEnum(
			finalStandardCompliant,
			MethodologyStandardsStandardCompliantEnum,
			'methodology standard compliance'
		),
		standardUsed:
			standardUsed &&
			convertToEnum(standardUsed, Standard, 'methodology standard'),
		...others,
	}
}

export const convertDraftToMethodologies = (
	draftData?: DraftMethodologies | Methodologies
): Methodologies => {
	if (!draftData) {
		return {
			methodologyStandards: {
				standardCompliant: MethodologyStandardsStandardCompliantEnum.UNKNOWN,
			},
		}
	}
	const {
		methodologyStandards,
		impactAssessmentMethod,
		collectionStartDate,
		collectionEndDate,
		validityStartDate,
		validityEndDate,
		...others
	} = draftData
	return {
		methodologyStandards:
			convertDraftToMethodologyStandards(methodologyStandards),
		impactAssessmentMethod: impactAssessmentMethod?.map(method =>
			convertToEnum(
				method,
				MethodologiesImpactAssessmentMethodEnum,
				'impact assessment method'
			)
		),
		collectionStartDate: collectionStartDate
			? collectionStartDate.includes('T')
				? collectionStartDate
				: new Date(collectionStartDate + 'T00:00:00Z').toISOString()
			: undefined,
		collectionEndDate: collectionEndDate
			? collectionEndDate.includes('T')
				? collectionEndDate
				: new Date(collectionEndDate + 'T00:00:00Z').toISOString()
			: undefined,
		validityStartDate: validityStartDate
			? validityStartDate.includes('T')
				? validityStartDate
				: new Date(validityStartDate + 'T00:00:00Z').toISOString()
			: undefined,
		validityEndDate: validityEndDate
			? validityEndDate.includes('T')
				? validityEndDate
				: new Date(validityEndDate + 'T00:00:00Z').toISOString()
			: undefined,
		...others,
	}
}

const convertDraftToEmissionValuesLocation = (
	draftData?: DraftEmissionValuesLocation | EmissionValuesLocation
): EmissionValuesLocation | undefined => {
	if (!draftData) {
		return undefined
	}
	const { country, locationType, region, manufacturingLocation, ...others } =
		draftData

	// Default to GLOBAL if location type is missing to prevent blocking users
	const finalLocationType =
		locationType || EmissionValuesLocationLocationTypeEnum.GLOBAL

	return {
		country:
			country &&
			convertToEnum(country, EmissionValuesLocationCountryEnum, 'country'),
		locationType: convertToEnum(
			finalLocationType,
			EmissionValuesLocationLocationTypeEnum,
			'location type'
		),
		region:
			region &&
			convertToEnum(region, EmissionValuesLocationRegionEnum, 'region'),
		manufacturingLocation: manufacturingLocation?.locationType && {
			country:
				manufacturingLocation.country &&
				convertToEnum(
					manufacturingLocation.country,
					ManufacturingLocationCountryEnum,
					'country'
				),
			locationType: convertToEnum(
				manufacturingLocation.locationType,
				ManufacturingLocationLocationTypeEnum,
				'location type'
			),
			region:
				manufacturingLocation.region &&
				convertToEnum(
					manufacturingLocation.region,
					ManufacturingLocationRegionEnum,
					'region'
				),
		},

		...others,
	}
}

const convertLifeCycleBreakdownItem = (
	name: string,
	draftData: DraftEmissionValueItem | EmissionValueItem
): EmissionValueItem => {
	const { emissionType, lifecycleStage, unit, value, ...others } = draftData
	if (value === undefined) {
		throw new Error(`Value is not set for ${name} breakdown`)
	}
	if (!unit) {
		throw new Error(`Unit is not set for ${name} breakdown`)
	}
	return {
		lifecycleStage:
			lifecycleStage &&
			convertToEnum(
				lifecycleStage,
				LifecycleStage,
				`lifecycle stage for ${name} breakdown`
			),
		emissionType:
			emissionType &&
			convertToEnum(
				emissionType,
				EmissionType,
				`emission type in ${name} breakdown`
			),
		unit: convertToEnum(unit, EmissionValueUnit, `unit in ${name} breakdown`),
		value,
		...others,
	}
}

const convertLifeCycleBreakdown = (
	name: string,
	draftData?: readonly EmissionValueItem[] | readonly DraftEmissionValueItem[]
): readonly EmissionValueItem[] => {
	if (!draftData?.length) {
		return []
	}
	// Filter out items with undefined values before converting
	return draftData
		.filter(item => item.value !== undefined)
		.map(item => convertLifeCycleBreakdownItem(name, item))
}

const convertDraftToEmissionValuesBreakdown = (
	draftData?: DraftEmissionsValuesBreakdown | EmissionsValuesBreakdown
): EmissionsValuesBreakdown | undefined => {
	if (!draftData) {
		return undefined
	}
	const {
		materialAcquisitionAndPreprocessing,
		upstreamTransportation,
		production,
		distributionAndStorage,
		productUse,
		endOfLife,
		dataUsed,
		unit,
		...others
	} = draftData
	return {
		materialAcquisitionAndPreprocessing: convertLifeCycleBreakdown(
			'materialAcquisitionAndPreprocessing',
			materialAcquisitionAndPreprocessing
		),
		upstreamTransportation: convertLifeCycleBreakdown(
			'upstreamTransportation',
			upstreamTransportation
		),
		production: convertLifeCycleBreakdown('production', production),
		distributionAndStorage: convertLifeCycleBreakdown(
			'distributionAndStorage',
			distributionAndStorage
		),
		productUse: convertLifeCycleBreakdown('productUse', productUse),
		endOfLife: convertLifeCycleBreakdown('endOfLife', endOfLife),
		dataUsed:
			dataUsed &&
			convertToEnum(dataUsed, EmissionValueDataUsed, 'data-used value'),
		unit: unit && convertToEnum(unit, EmissionValueUnit, 'unit'),
		...others,
	}
}

const convertDraftToEmissionValues = (
	draftData?: DraftEmissionValues | EmissionValues
): EmissionValues => {
	if (!draftData) {
		throw new Error('Missing emission values')
	}
	const {
		confidence,
		emissionValuesGranularity,
		emissionValuesLocation,
		emissionsValuesBreakdown,
		emissionsAttachments,
		allocationMethodUsed,
		...others
	} = draftData

	// Auto-detect granularity if missing to prevent blocking users
	let finalGranularity = emissionValuesGranularity
	if (!finalGranularity) {
		const breakdown = emissionsValuesBreakdown
		const mat = breakdown?.materialAcquisitionAndPreprocessing
		const prod = breakdown?.production
		const dist = breakdown?.distributionAndStorage

		// Check if detailed lifecycle mode (multiple items per stage)
		const isDetailedMode =
			(mat?.length ?? 0) > 1 ||
			(prod?.length ?? 0) > 1 ||
			(dist?.length ?? 0) > 1

		if (isDetailedMode) {
			finalGranularity = EmissionValueGranularityLevel.DETAILED_LIFECYCLE
		} else {
			// Check if simple lifecycle breakdown is filled (all three stages have values)
			const hasBreakdown =
				mat?.[0]?.value !== undefined &&
				prod?.[0]?.value !== undefined &&
				dist?.[0]?.value !== undefined

			finalGranularity = hasBreakdown
				? EmissionValueGranularityLevel.LIFECYCLE
				: EmissionValueGranularityLevel.PRODUCT
		}
	}

	return {
		confidence:
			confidence &&
			convertToEnum(confidence, ConfidenceLevel, 'confidence level'),
		emissionValuesGranularity: convertToEnum(
			finalGranularity,
			EmissionValueGranularityLevel,
			'granularity'
		),
		emissionValuesLocation: convertDraftToEmissionValuesLocation(
			emissionValuesLocation
		),
		emissionsValuesBreakdown: convertDraftToEmissionValuesBreakdown(
			emissionsValuesBreakdown
		),
		emissionsAttachments: emissionsAttachments?.map(
			convertDraftToEmissionsAttachment
		),
		allocationMethodUsed: convertToEnum(
			allocationMethodUsed,
			DraftEmissionValuesAllocationMethodUsedEnum,
			'allocation method',
			true
		),
		...others,
	}
}

const convertDraftToVerificationAttachment = (
	draftData: VerificationAttachment | DraftVerificationAttachment
): VerificationAttachment => {
	const { id, name, ...other } = draftData
	if (!id) {
		throw new Error('id is missing in a verification file')
	}
	if (!name) {
		throw new Error('name is missing in a verification file')
	}
	return { id, name, ...other }
}

const convertDraftToEmissionsAttachment = (
	draftData: EmissionsAttachment | DraftEmissionsAttachment
): EmissionsAttachment => {
	const { id, name, ...other } = draftData
	if (!id) {
		throw new Error('id is missing in a emissions attachement file')
	}
	if (!name) {
		throw new Error('name is missing in a emissions attachement file')
	}
	return { id, name, ...other }
}

const convertDraftToVerificationData = (
	draftData?: VerificationData | DraftVerificationData
): VerificationData | undefined => {
	if (!draftData) {
		return draftData
	}
	const {
		isDataVerified,
		verificationAttachments,
		verificationProvider,
		verificationStandard,
		...others
	} = draftData
	if (isDataVerified === undefined) {
		return undefined
	}
	return {
		isDataVerified,
		verificationAttachments: verificationAttachments?.map(
			convertDraftToVerificationAttachment
		),
		verificationProvider:
			verificationProvider &&
			convertToEnum(
				verificationProvider,
				VerificationProvider,
				'verification provider'
			),
		verificationStandard:
			verificationStandard &&
			convertToEnum(verificationStandard, Standard, 'verification standard'),
		...others,
	}
}

export const convertDraftToEmissionRequest = (
	draftData: DraftEmissionRequestFormData | EmissionRequestProductFormData
): EmissionRequestProductFormData => {
	const {
		dataSources,
		emissionValues,
		methodologies,
		verificationData,
		reductionInitiatives,
		...others
	} = draftData
	return {
		dataSources: convertDraftToDataSources(dataSources),
		emissionValues: convertDraftToEmissionValues(emissionValues),
		methodologies: convertDraftToMethodologies(methodologies),
		verificationData: convertDraftToVerificationData(verificationData),
		reductionInitiatives: reductionInitiatives?.map(initiative =>
			Object.assign({}, initiative, {
				initiativeLevel: InitiativeLevel.PRODUCT,
			})
		),
		...others,
	}
}
